import { useState } from 'react';
import { X } from 'lucide-react';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import type { PbiReviewStatus } from '../../types/powerBiUsage';
import { PBI_REVIEW_META } from './status';

interface Props {
    target: PbiReviewStatus;
    reportName: string;
    initialNote: string | null;
    busy: boolean;
    onConfirm: (payload: { status: PbiReviewStatus; statusNote: string | null }) => void;
    onCancel: () => void;
}

/**
 * The deliberate-action step for Keep/Retire: a short note explaining the decision.
 * (Undecided is set directly from the menu — resetting needs no justification.)
 */
export function PowerBiReviewModal({ target, reportName, initialNote, busy, onConfirm, onCancel }: Props) {
    const [note, setNote] = useState(initialNote ?? '');
    useTopmostEscape(true, onCancel);
    const meta = PBI_REVIEW_META[target];
    const Icon = meta.icon;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onCancel}>
            <div
                className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                            <Icon className={`h-4 w-4 ${meta.iconClass}`} />
                            Set review to “{meta.label}”
                        </h2>
                        <p className="mt-0.5 truncate text-sm text-gray-500" title={reportName}>{reportName}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <label className="mt-3 block text-xs font-medium text-gray-600">
                    Note (why — shown in the decision trail)
                </label>
                <textarea
                    value={note}
                    onChange={e => setNote(e.target.value.slice(0, 500))}
                    rows={3}
                    autoFocus
                    placeholder={target === 'Retire' ? 'e.g. superseded by Sales Controlling page, no views in 6 months' : 'optional'}
                    className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm text-gray-800"
                />
                <p className="mt-0.5 text-right text-[10px] text-gray-400">{note.length}/500</p>

                <div className="mt-3 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm({ status: target, statusNote: note.trim() || null })}
                        disabled={busy}
                        className={`h-9 rounded-md px-3 text-sm font-medium text-white disabled:opacity-50 ${
                            target === 'Retire' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                        }`}
                    >
                        {busy ? 'Saving…' : meta.label}
                    </button>
                </div>
            </div>
        </div>
    );
}
